import { addMultipleMaterials } from '../../api/folders/add-multiple-materials';
import { addSingleMaterial } from '../../api/folders/add-single-material';
import { archiveMaterials, unarchiveMaterials } from '../../api/folders/archive-materials';
import { createFolder } from '../../api/folders/create-folder';
import { deleteFolder } from '../../api/folders/delete-folder';
import { getFolderMaterialCounts } from '../../api/folders/get-folder-material-counts';
import { postFolderMaterialsQuery } from '../../api/folders/get-folder-materials';
import { updateFolder } from '../../api/folders/update-folder';
import API_URLS from '../../constants/api-urls';
import { FOLDER_DIRECTORIES } from '../../constants/folder';
import fetchAllEntities from '../../utils/fetch-all-entities';

type FoldersByMaterialIdMap = Record<number, Folder[]>;

type FoldersBatch = {
    materialIds: Set<number>;
    promise: Promise<FoldersByMaterialIdMap> | null;
    timeoutId: ReturnType<typeof setTimeout> | null;
};

const foldersBatches: Partial<Record<FOLDER_DIRECTORIES, FoldersBatch>> = {};

const fetchFoldersByMaterialIds = (
    directory: FOLDER_DIRECTORIES,
    materialIds: number[]
): Promise<FoldersByMaterialIdMap> => {
    if (!materialIds.length) {
        return Promise.resolve({});
    }

    const queryBody = materialIds
        .map(
            (materialId) => `
            material_${materialId}: folders_materials (
                filter: { material_id: ${materialId}, folders__type: ${directory} }
                pagination: { limit: 200 }
            ) {
                folders {
                    id
                    is_archive
                    title
                }
            }
        `
        )
        .join('\n');

    const query = `
        query {
            ${queryBody}
        }
    `;

    return postFolderMaterialsQuery(directory, query).then(({ data }) => {
        const result: FoldersByMaterialIdMap = {};

        materialIds.forEach((materialId) => {
            const list = data[`material_${materialId}`] || [];
            const serializedList = list.map((item: { folders: Folder[] }) => item.folders[0]);

            result[materialId] = serializedList;
        });

        return result;
    });
};

const foldersService = {
    addMultipleMaterials,
    addSingleMaterial,
    archive: archiveMaterials,
    create: createFolder,
    delete: deleteFolder,
    get: (directory: FOLDER_DIRECTORIES) => {
        const query = `
            query {
                folders (
                    filter: { type: ${directory} }
                    order_by: { sort: id, order: ASC }
                    pagination: { limit: 200 }
                ) {
                   id
                   title
                   parent_id
                   user_id
                   is_archive
                   is_shared
                   _total
                }
            }
        `;

        const config = {
            params: {
                type: directory
            }
        };

        return fetchAllEntities(API_URLS.FOLDERS.GET, query, 'folders', config);
    },
    getFoldersByMaterialId: (directory: FOLDER_DIRECTORIES, materialId: number) => {
        const query = `
            query {
                folders_materials (
                    filter: { material_id: ${materialId}, folders__type: ${directory} }
                    pagination: { limit: 200 }
                ) {
                    folders {
                        id
                        is_archive
                        title
                    }
                }
            }
        `;

        return new Promise((resolve) => {
            postFolderMaterialsQuery(directory, query).then((response) => {
                const list = response.data.folders_materials;
                const serializedList = list.map((item: { folders: Folder[] }) => item.folders[0]);

                resolve(serializedList);
            });
        });
    },
    getFoldersByMaterialIds: (directory: FOLDER_DIRECTORIES, materialIds: number[]) => {
        if (!materialIds.length) {
            return Promise.resolve({});
        }

        let batch = foldersBatches[directory];

        if (!batch) {
            batch = {
                materialIds: new Set<number>(),
                promise: null,
                timeoutId: null
            };

            foldersBatches[directory] = batch;
        }

        materialIds.forEach((id) => batch!.materialIds.add(id));

        if (!batch.promise) {
            batch.promise = new Promise<FoldersByMaterialIdMap>((resolve, reject) => {
                batch!.timeoutId = setTimeout(() => {
                    const ids = Array.from(batch!.materialIds);

                    batch!.materialIds = new Set<number>();
                    batch!.timeoutId = null;
                    batch!.promise = null;

                    fetchFoldersByMaterialIds(directory, ids).then(resolve).catch(reject);
                }, 0);
            });
        }

        return batch.promise;
    },
    getMaterialCounts: getFolderMaterialCounts,
    unarchive: unarchiveMaterials,
    update: updateFolder
};

export default foldersService;
